/**
 * In-app agent turn SSE stream — start turn, subscribe to events, submit FE tool results.
 */

import api from "@/services/api/client";
import type {
  FeContext,
  FeToolResult,
  MessageAttachment,
} from "@/services/api/in-app-agent";
import { getDeploymentRoutingHeaders } from "@/utils/deploymentRouting";
import { getBearerTokenFromAuthData } from "@/utils/wsAuth";

export interface StartTurnPayload {
  message_content: string;
  worker_id?: string;
  fe_context: FeContext;
  is_subsequent?: boolean;
  tool_results?: FeToolResult[];
  attachments?: MessageAttachment[];
  profile_company_id?: string;
}

export interface TurnCreatedResponse {
  turn_id: string;
}

export interface FeToolCallEvent {
  tool_id: string;
  params: Record<string, unknown>;
  instructions?: string;
}

export type ToolStepKind = "backend" | "fe" | "mcp";

export type ToolStepStatus = "running" | "success" | "error" | "cancelled";

export type TurnToolStepSegment = {
  type: "tool_step";
  step_id: string;
  tool_name: string;
  kind: ToolStepKind;
  label: string;
  status: ToolStepStatus;
  summary?: string | null;
  mcp_connector_id?: string | null;
};

export type TurnTimelineSegment =
  | { type: "text"; content: string }
  | TurnToolStepSegment
  | { type: "embeds"; embeds: Record<string, unknown>[] };

export type TurnEventPayload =
  | { type: "turn.started"; interaction_id?: string | null; session_id?: string | null }
  | { type: "assistant.delta"; text: string; segment_index: number }
  | {
      type: "assistant.message";
      content: string;
      embeds?: Record<string, unknown>[];
      continue_after_tools?: boolean;
    }
  | {
      type: "tool.step";
      tool_name: string;
      tool_label?: string | null;
      status?: string | null;
      summary?: string | null;
      mcp_connector_id?: string | null;
    }
  | {
      type: "fe.tools_requested";
      request_id: string;
      fe_tool_calls: FeToolCallEvent[];
      continue_after_tools?: boolean;
    }
  | { type: "turn.completed"; interaction_id?: string | null }
  | { type: "turn.failed"; error: string; code?: string | null }
  | { type: "turn.cancelled"; reason?: string | null }
  | { type: "heartbeat" }
  | { type: "turn.status"; label: string; detail?: string | null }
  | {
      type: "turn.segment";
      segment_index: number;
      segment: TurnTimelineSegment;
    };

export interface TurnEventEnvelope {
  schema_version: number;
  seq: number;
  turn_id: string;
  emitted_at: string;
  event: TurnEventPayload;
}

export interface TurnEventHandlers {
  onEnvelope?: (envelope: TurnEventEnvelope) => void;
  onStarted?: (event: Extract<TurnEventPayload, { type: "turn.started" }>) => void | Promise<void>;
  onAssistantDelta?: (event: Extract<TurnEventPayload, { type: "assistant.delta" }>) => void | Promise<void>;
  onAssistantMessage?: (event: Extract<TurnEventPayload, { type: "assistant.message" }>) => void | Promise<void>;
  onToolStep?: (event: Extract<TurnEventPayload, { type: "tool.step" }>) => void | Promise<void>;
  onFeToolsRequested?: (event: Extract<TurnEventPayload, { type: "fe.tools_requested" }>) => void | Promise<void>;
  onCompleted?: (event: Extract<TurnEventPayload, { type: "turn.completed" }>) => void | Promise<void>;
  onFailed?: (event: Extract<TurnEventPayload, { type: "turn.failed" }>) => void | Promise<void>;
  onCancelled?: (event: Extract<TurnEventPayload, { type: "turn.cancelled" }>) => void | Promise<void>;
  onHeartbeat?: () => void | Promise<void>;
  onTurnSegment?: (event: Extract<TurnEventPayload, { type: "turn.segment" }>) => void | Promise<void>;
}

export interface TurnEventSubscription {
  close: () => void;
  done: Promise<void>;
}

const TERMINAL_EVENT_TYPES = new Set([
  "turn.completed",
  "turn.failed",
  "turn.cancelled",
]);

function buildAuthHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    Accept: "text/event-stream",
  };
  const token = getBearerTokenFromAuthData();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  try {
    Object.assign(headers, getDeploymentRoutingHeaders());
  } catch {
    // deployment routing optional in tests
  }
  return headers;
}

function resolveTurnEventsUrl(turnId: string, afterSeq: number): string {
  const path = `/agentic/in-app-agent/turns/${encodeURIComponent(turnId)}/events`;
  const query = afterSeq > 0 ? `?after_seq=${afterSeq}` : "";
  // Long-lived SSE must go through the dev-server proxy (same origin) so the connection
  // is not dropped by cross-origin fetch to localhost:8001 from localhost:3001.
  if (typeof window !== "undefined") {
    return `${window.location.origin}/api/v1${path}${query}`;
  }
  const base = api.defaults.baseURL ?? "/api/v1";
  if (base.startsWith("/")) {
    return `${base.replace(/\/+$/, "")}${path}${query}`;
  }
  return `${base.replace(/\/+$/, "")}${path}${query}`;
}

const MAX_SSE_RECONNECTS = 8;

function parseSseEventBlock(block: string): TurnEventEnvelope | null {
  const dataLines = block
    .split("\n")
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trimStart());
  if (dataLines.length === 0) {
    return null;
  }
  try {
    return JSON.parse(dataLines.join("\n")) as TurnEventEnvelope;
  } catch {
    return null;
  }
}

function runEventHandler(handler: (() => void | Promise<void>) | undefined): void {
  if (!handler) {
    return;
  }
  try {
    const result = handler();
    if (result && typeof (result as Promise<void>).then === "function") {
      void (result as Promise<void>).catch((err) => {
        console.error("[turnStream] Event handler failed:", err);
      });
    }
  } catch (err) {
    console.error("[turnStream] Event handler failed:", err);
  }
}

async function dispatchEnvelope(envelope: TurnEventEnvelope, handlers: TurnEventHandlers): Promise<void> {
  handlers.onEnvelope?.(envelope);
  const event = envelope.event;
  switch (event.type) {
    case "turn.started":
      await handlers.onStarted?.(event);
      break;
    case "assistant.delta":
      await handlers.onAssistantDelta?.(event);
      break;
    case "assistant.message":
      await handlers.onAssistantMessage?.(event);
      break;
    case "tool.step":
      await handlers.onToolStep?.(event);
      break;
    case "fe.tools_requested":
      // Do not block the SSE read loop — FE tool runs can take seconds while more events queue up.
      runEventHandler(() => handlers.onFeToolsRequested?.(event));
      break;
    case "turn.completed":
      await handlers.onCompleted?.(event);
      break;
    case "turn.failed":
      await handlers.onFailed?.(event);
      break;
    case "turn.cancelled":
      await handlers.onCancelled?.(event);
      break;
    case "heartbeat":
      await handlers.onHeartbeat?.();
      break;
    case "turn.segment":
      await handlers.onTurnSegment?.(event);
      break;
    default:
      break;
  }
}

export async function startTurn(
  sessionId: string,
  payload: StartTurnPayload,
): Promise<TurnCreatedResponse> {
  const response = await api.post<TurnCreatedResponse>(
    `/agentic/in-app-agent/sessions/${encodeURIComponent(sessionId)}/turns`,
    payload,
  );
  return response.data;
}

export async function submitFeToolResults(
  turnId: string,
  requestId: string,
  results: FeToolResult[],
): Promise<void> {
  await api.post(
    `/agentic/in-app-agent/turns/${encodeURIComponent(turnId)}/fe-tool-results`,
    { request_id: requestId, results },
  );
}

export async function cancelTurn(turnId: string): Promise<void> {
  await api.post(`/agentic/in-app-agent/turns/${encodeURIComponent(turnId)}/cancel`);
}

function subscribeTurnEventsViaFetch(
  turnId: string,
  handlers: TurnEventHandlers,
): TurnEventSubscription {
  const controller = new AbortController();
  let resolveDone: () => void = () => {};
  let rejectDone: (err: unknown) => void = () => {};

  const done = new Promise<void>((resolve, reject) => {
    resolveDone = resolve;
    rejectDone = reject;
  });

  const run = async () => {
    let afterSeq = 0;
    let sawTerminal = false;

    for (let attempt = 0; attempt <= MAX_SSE_RECONNECTS; attempt += 1) {
      if (controller.signal.aborted) {
        resolveDone();
        return;
      }

      const response = await fetch(resolveTurnEventsUrl(turnId, afterSeq), {
        method: "GET",
        credentials: "include",
        headers: buildAuthHeaders(),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Turn event stream failed: ${response.status}`);
      }

      const body = response.body;
      if (!body) {
        throw new Error("Turn event stream has no body");
      }

      const reader = body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done: readDone, value } = await reader.read();
        if (readDone) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? "";

        for (const block of parts) {
          const envelope = parseSseEventBlock(block);
          if (!envelope) {
            continue;
          }
          afterSeq = Math.max(afterSeq, envelope.seq);
          await dispatchEnvelope(envelope, handlers);
          if (TERMINAL_EVENT_TYPES.has(envelope.event.type)) {
            sawTerminal = true;
            resolveDone();
            return;
          }
        }
      }

      if (controller.signal.aborted) {
        resolveDone();
        return;
      }
      if (sawTerminal) {
        resolveDone();
        return;
      }
    }

    rejectDone(new Error("Turn event stream ended without a terminal event"));
  };

  void run().catch((err) => {
    if (controller.signal.aborted) {
      resolveDone();
      return;
    }
    rejectDone(err);
  });

  return {
    close: () => controller.abort(),
    done,
  };
}

export function subscribeTurnEvents(
  turnId: string,
  handlers: TurnEventHandlers,
): TurnEventSubscription {
  // Fetch streaming supports Bearer + deployment routing headers; native EventSource cannot.
  return subscribeTurnEventsViaFetch(turnId, handlers);
}
